import {
  IRechargeLocationData,
} from "libs/recharge-client/src/lib/model/RechargeCommon";
import {
  IRechargeOrder,
} from "libs/recharge-client/src/lib/model/RechargeOrder";
import {
  IOrderReceiptResponse,
  IRechargeOrderReceiptData,
  IChargeReceiptData,
  TOrderReceiptData,
  IDiscountDetail,
} from "@api-clients/orders";
import Decimal from "decimal.js";
import { getCountryNameInEnglish } from "@./address";
import { toRecipeProductTitle } from "libs/api/customer/src/lib/types/products";
import {
  normalizeProductTitle,
} from "libs/discount-calculation/src/util/product";

import {
  ChargeBillingAddress,
  ChargeShippingAddress,
  SubscriptionItemVariantValue,
  SubscriptionItemType,
  OrderFulfilment,
  OrderFulfilmentStatus,
  DiscountApplicationToDiscountCharge,
  DiscountApplication,
  DiscountCode,
} from "@/primary-models";

const rechargeAddressDataToString = (address: IRechargeLocationData): string => [
  address.address1,
  address.address2,
  address.city,
  address.zip,
].filter(Boolean).join(", ");

const chargeAddressToString = (address: ChargeBillingAddress | ChargeShippingAddress | null): string => {
  if (!address) { return ""; }
  return [
    address.address1,
    address.address2,
    address.city,
    getCountryNameInEnglish(address.countryCode),
    address.postcode,
  ].filter(Boolean).join(", ");
};

/**
 * Assumes that all orders in the fulfilment order have the same status.
 * Returns null if the status is not the same for all orders.
 */
const getFulfillmentStatus = (fulfilmentOrder: OrderFulfilment[]): OrderFulfilmentStatus | null => {
  const statuses = new Set(fulfilmentOrder.map((order) => order.status));
  return statuses.size === 1 ? fulfilmentOrder[0].status : null;
};

const getDiscountDetailFromRawRechargeOrder = (rechargeOrder: IRechargeOrder): IDiscountDetail[] => rechargeOrder
  .discounts.map((d) => ({
    amount: new Decimal(d.value),
    code: d.code,
  }));

const getDiscountDetailFromDb = (discountApplicationToDiscountCharge: (DiscountApplicationToDiscountCharge & {
  discountApplication: DiscountApplication & {
    discount: DiscountCode
  }
})[]): IDiscountDetail[] => discountApplicationToDiscountCharge.map((d) => ({
  amount: d.discountAmount,
  code: d.discountApplication.discount.code,
}));

const transformRechargeOrderToOrderReceipt = ({ order }: IRechargeOrderReceiptData): IOrderReceiptResponse => {
  const shippingAddress = order.shippingAddress as IRechargeLocationData;
  const billingAddress = order.billingAddress as IRechargeLocationData;

  const rechargeOrder: IRechargeOrder = order.raw as unknown as IRechargeOrder;

  const totalRefundedAmount = new Decimal(rechargeOrder.total_refunds);

  const deliveryFee = order.items
    .filter((item) => normalizeProductTitle(item.productTitle) === "DPD Delivery Fee")
    .reduce((acc, item) => acc.add(new Decimal(item.unitPrice ?? 0).times(item.quantity)), new Decimal(0));

  const discounts = order.discountApplicationToDiscountCharge.length === 0
    ? getDiscountDetailFromRawRechargeOrder(rechargeOrder)
    : getDiscountDetailFromDb(order.discountApplicationToDiscountCharge);

  return {
    rechargeOrderId: order.id,
    katkinChargeId: null,
    rechargeChargeId: order.rechargeChargeId,
    chargeDate: order.processedAt,
    deliveryDate: order.deliveryDate,
    boxNumber: order.orderFulfilment?.[0]?.subscriptionBoxNumber,
    shippingAddress: rechargeAddressDataToString(shippingAddress),
    billingAddress: rechargeAddressDataToString(billingAddress),
    totalDiscount: new Decimal((order.raw as unknown as IRechargeOrder).total_discounts),
    tax: order.totalTax,
    totalPrice: order.totalPrice,
    deliveryFee,
    status: getFulfillmentStatus(order.orderFulfilment),
    refundedAmount: totalRefundedAmount.isNaN() || totalRefundedAmount.isZero() ? null : totalRefundedAmount,
    items: order.items
      .filter((item) => normalizeProductTitle(item.productTitle) !== "AddOn Plan"
        && normalizeProductTitle(item.productTitle) !== "DPD Delivery Fee")
      .map((item) => ({
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        title: toRecipeProductTitle(item.productTitle) ?? item.productTitle as SubscriptionItemType,
        variant: item.variantTitle as SubscriptionItemVariantValue | null,
      })),
    discounts,
  };
};

const transformChargeToOrderReceipt = ({ charge }: IChargeReceiptData): IOrderReceiptResponse => ({
  rechargeOrderId: charge.rechargeOrderId,
  katkinChargeId: charge.chargeId,
  rechargeChargeId: charge.rechargeChargeId,
  chargeDate: charge.finalisedAt,
  deliveryDate: charge.deliveryDate,
  status: getFulfillmentStatus(charge.fulfilmentOrder),
  boxNumber: Math.max(...charge.items.map((item) => item.orderCount)),
  billingAddress: chargeAddressToString(charge.billingAddress),
  shippingAddress: chargeAddressToString(charge.shippingAddress),
  tax: charge.taxAmountDiscounted,
  totalPrice: charge.totalPriceDiscounted,
  totalDiscount: charge.totalDiscountAmount,
  deliveryFee: charge.deliveryFeeDiscounted,
  refundedAmount: charge.refundAmount,
  items: charge.items.map((item) => ({
    unitPrice: item.originalUnitPrice,
    title: item.subscriptionItemType,
    quantity: item.quantity,
    variant: item.subscriptionItemVariantValue,
  })),
  discounts: charge.discounts.map((discount) => ({
    amount: discount.discountAmount,
    code: discount.discountApplication.discount.code,
  })),
});

export const transformOrderOrChargeToOrderReceipt = (data: TOrderReceiptData): IOrderReceiptResponse => {
  if (data.old) {
    return transformRechargeOrderToOrderReceipt(data);
  }
  return transformChargeToOrderReceipt(data);
};

export const transformOrdersAndChargesToOrderReceipts = (
  ordersAndCharges: TOrderReceiptData[],
): IOrderReceiptResponse[] => ordersAndCharges
  .map(transformOrderOrChargeToOrderReceipt)
  .sort((a, b) => (b.chargeDate?.getTime() ?? 0) - (a.chargeDate?.getTime() ?? 0));
